import { PropsWithChildren, ReactNode } from "react"

import { TableHeaderGroupToggle } from "./TableHeaderGroupToggle"
import {
  TableCell,
  TableCellProps,
} from "../../../../components/composite/TableV2/TableCell"
import { classNames } from "../../../../lib/classNames"

export interface TableHeaderGroupCellProps
  extends Omit<
    Extract<TableCellProps, { as: "th" }>,
    "width" | "as" | "children" | "title"
  > {
  colSpan: number
  title?: ReactNode
  accessory?: ReactNode
  isExpandable?: boolean
  isExpanded?: boolean
  onToggleExpanded?: () => void
}

export function TableHeaderGroupCell({
  className,
  colSpan,
  title,
  accessory,
  isExpandable,
  isExpanded,
  onToggleExpanded,
  ...otherProps
}: TableHeaderGroupCellProps) {
  return (
    <TableCell
      as="th"
      className={classNames("color-subtle text-caption", className)}
      colSpan={colSpan}
      {...otherProps}
    >
      <ToggleWrapper
        title={title}
        isExpandable={isExpandable}
        isExpanded={isExpanded}
        onToggleExpanded={onToggleExpanded}
      >
        <TitleWithAccessory title={title} accessory={accessory} />
      </ToggleWrapper>
    </TableCell>
  )
}

type ToggleWrapperProps = PropsWithChildren<
  Pick<
    TableHeaderGroupCellProps,
    "title" | "isExpandable" | "isExpanded" | "onToggleExpanded"
  >
>

function ToggleWrapper({
  title,
  isExpandable,
  isExpanded,
  onToggleExpanded,
  children,
}: ToggleWrapperProps) {
  if (!isExpandable) {
    return children
  }

  return (
    <div className="flex items-center gap-x-4">
      <div className="truncate text-clip">{children}</div>
      <TableHeaderGroupToggle
        className="shrink-0"
        data-testid={
          typeof title === "string"
            ? `${title.toLowerCase()}-header-group-toggle`
            : "header-group-toggle"
        }
        isExpanded={isExpanded}
        onToggle={onToggleExpanded}
      />
    </div>
  )
}

type TitleWithAccessoryProps = Pick<
  TableHeaderGroupCellProps,
  "title" | "accessory"
>

function TitleWithAccessory({ title, accessory }: TitleWithAccessoryProps) {
  if (accessory) {
    return (
      <div className="flex items-center gap-x-2">
        {title}
        {accessory}
      </div>
    )
  }

  return <>{title}</>
}
